import { useState, useEffect } from 'react';
import { GameController, ChatsCircle, Trophy, MonitorPlay, Microphone, Users } from '@phosphor-icons/react';

export const AnimatedDemo = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);

  const features = [
    { icon: <ChatsCircle size={24} weight="fill" />, title: 'Real-Time Chat', color: '#0047FF' },
    { icon: <MonitorPlay size={24} weight="fill" />, title: 'Watch Streams', color: '#10B981' },
    { icon: <Trophy size={24} weight="fill" />, title: 'Leaderboards', color: '#F59E0B' },
    { icon: <Microphone size={24} weight="fill" />, title: 'Voice Notes', color: '#8B5CF6' },
  ];

  const demoMessages = [
    { user: 'Alex', message: 'Just hit Diamond rank! 🎮', avatar: 'A' },
    { user: 'Sarah', message: 'GG! That was an insane match', avatar: 'S' },
    { user: 'Mike', message: 'Who wants to queue up for ranked?', avatar: 'M' },
    { user: 'You', message: 'Count me in! Let\'s go 🔥', avatar: 'Y', isOwn: true },
  ];

  // Cycle through features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Typing animation for messages
  useEffect(() => {
    const currentMessage = demoMessages[messageIndex];
    let charIndex = 0;
    setTypingText('');

    const typingInterval = setInterval(() => {
      if (charIndex < currentMessage.message.length) {
        setTypingText(currentMessage.message.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setMessageIndex((prev) => (prev + 1) % demoMessages.length);
        }, 2000);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, [messageIndex]);

  return (
    <div className="bg-[#09090B] p-1 shadow-2xl">
      {/* Browser Chrome */}
      <div className="bg-[#1a1a1a] px-4 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
          <div className="w-3 h-3 rounded-full bg-[#FFBD2E]"></div>
          <div className="w-3 h-3 rounded-full bg-[#28CA41]"></div>
        </div>
        <div className="flex-1 text-center">
          <span className="text-xs text-white/50">DashLink Gaming - Live Demo</span>
        </div>
      </div>

      <div className="bg-[#09090B] p-6">
        {/* Feature Showcase */}
        <div className="flex justify-center gap-4 mb-8">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`flex items-center gap-2 px-4 py-2 transition-all duration-500 ${
                activeFeature === index
                  ? 'bg-white text-[#09090B] scale-110'
                  : 'bg-white/10 text-white/60'
              }`}
            >
              <span style={{ color: activeFeature === index ? feature.color : 'inherit' }}>
                {feature.icon}
              </span>
              <span className="text-sm font-medium">{feature.title}</span>
            </div>
          ))}
        </div>

        {/* Live Chat Demo */}
        <div className="bg-white max-w-2xl mx-auto">
          <div className="p-4 border-b border-[#E4E4E7] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#0047FF] p-2">
                <GameController size={20} weight="fill" color="#FFFFFF" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#09090B]">Valorant Chat</h3>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-xs text-[#71717A]">1,234 online</span>
                </div>
              </div>
            </div>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-[#0047FF] text-white text-xs">
              <MonitorPlay size={14} weight="fill" />
              Watch Stream
            </button>
          </div>

          <div className="p-4 h-48 space-y-3">
            {demoMessages.slice(0, messageIndex + 1).map((msg, index) => (
              <div
                key={`${msg.user}-${index}`}
                className={`flex items-start gap-3 animate-fade-in ${
                  msg.isOwn ? 'flex-row-reverse' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  msg.isOwn ? 'bg-[#0047FF]' : 'bg-[#71717A]'
                }`}>
                  {msg.avatar}
                </div>
                <div className={`max-w-xs ${msg.isOwn ? 'text-right' : ''}`}>
                  <span className="text-xs font-medium text-[#09090B]">{msg.user}</span>
                  <div className={`mt-1 px-3 py-2 text-sm ${
                    msg.isOwn
                      ? 'bg-[#0047FF] text-white'
                      : 'bg-[#F4F4F5] text-[#09090B]'
                  }`}>
                    {index === messageIndex ? typingText : msg.message}
                    {index === messageIndex && <span className="animate-pulse">|</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-[#E4E4E7]">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 border border-[#E4E4E7] text-sm bg-[#FAFAFA]"
                disabled
              />
              <button className="px-4 py-2 bg-[#0047FF] text-white text-sm font-medium">
                Send
              </button>
            </div>
          </div>
        </div>

        <p className="text-center mt-8 text-xs font-mono uppercase tracking-widest text-white/50" data-testid="demo-sample-label">
          Animated demo · sample data
        </p>
      </div>
    </div>
  );
};
